'use client';

import { useState } from 'react';
import { PortableText, PortableTextBlock } from '@portabletext/react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import EastRounded from '@mui/icons-material/EastRounded';
import useTranslations from '@/hooks/useTranslations';
import Image from './Image';
import Typography from '@mui/material/Typography';
import { HIDE_ANNOUNCEMENT_ID } from './Announcement';
import { useRouter } from 'next/navigation';
import { Link } from '@/blocks/components/types';

interface PopupProps {
  title: string;
  description: PortableTextBlock[];
  image?: string;
  id: string;
  submitText?: string;
  hideRemovePopup?: boolean;
  primaryCta?: Link;
}

export default function Popup({
  title,
  description,
  image,
  id,
  submitText,
  hideRemovePopup,
  primaryCta,
}: PopupProps) {
  const [open, setOpen] = useState(() => {
    if (hideRemovePopup) return true;
    // Check if the user has chosen not to see instructions for this product
    if (typeof window === 'undefined') return true; // Default to open on server
    const hideInstructions = localStorage.getItem(id);
    return hideInstructions !== 'true';
  });
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const { t, locale } = useTranslations();
  const router = useRouter();

  const handleClose = (buttonClick: boolean) => {
    if (dontShowAgain) {
      // Save user preference in localStorage using the product id
      localStorage.setItem(id, 'true');
    }

    if (buttonClick && primaryCta) {
      const isExternalUrl =
        primaryCta.href.startsWith('http://') || primaryCta.href.startsWith('https://');
      const url = isExternalUrl
        ? primaryCta.href
        : `${process.env.NEXT_PUBLIC_LINK_MARKETING_WEBSITE ?? '/'}${locale}${primaryCta.href}`;

      if (primaryCta.newTab) {
        window.open(url, '_blank');
      } else {
        if (isExternalUrl) {
          window.location.href = url;
        } else {
          router.push(url);
        }
      }
    } else {
      setOpen(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(false)}
      // With no "don't show again" there is no stored choice, so the server already knows it is
      // open: render it in place, without the fade-in, to paint it with the page, not after hydration
      disablePortal={hideRemovePopup}
      slotProps={{
        transition: { appear: !hideRemovePopup },
        backdrop: {
          sx: {
            backdropFilter: 'blur(5px)',
          },
          slotProps: { transition: { appear: !hideRemovePopup } },
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {title}
        <IconButton
          onClick={() => handleClose(false)}
          size='small'
          sx={{ position: 'absolute', right: 'var(--mui-spacing)' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <PortableText
          value={description}
          components={{
            types: {},
            marks: {},
            block: {
              normal: ({ children }) => (
                <Typography sx={id === HIDE_ANNOUNCEMENT_ID ? { mb: 1 } : { my: '16px' }}>
                  {children}
                </Typography>
              ),
            },
          }}
        />

        {image && (
          <Image
            src={image}
            alt={'Announcement image'}
            aspectRatio='16/10'
            // The dialog paper (max 600px, 32px margins) minus the content padding
            sizes='(max-width: 664px) calc(100vw - 112px), 552px'
            sx={{ borderRadius: '1rem' }}
          />
        )}
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          pb: 1,
          px: 1,
        }}
      >
        {!hideRemovePopup && (
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
              />
            }
            color='primary'
            label={t('components.doNotShowAgain')}
          />
        )}

        {(!primaryCta || !primaryCta.hidden) && (
          <Button
            onClick={() => handleClose(true)}
            color='secondary'
            className='rounded'
            endIcon={<EastRounded />}
            variant='text'
            sx={{ marginLeft: 'auto' }}
          >
            {submitText ?? primaryCta.text}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
